import { ChangeEvent, useContext } from 'react'

import Field from '@ors/components/manage/Form/Field'
import SimpleInput from '@ors/components/manage/Blocks/Section/ReportInfo/SimpleInput'
import { Label } from '@ors/components/manage/Blocks/BusinessPlans/BPUpload/helpers'
import { getOptionLabel } from '@ors/components/manage/Blocks/BusinessPlans/BPEdit/editSchemaHelpers'
import {
  DateInput,
  FormattedNumberInput,
} from '@ors/components/manage/Blocks/Replenishment/Inputs'
import ProjectsDataContext from '@ors/contexts/Projects/ProjectsDataContext'
import { STYLE } from '../../Replenishment/Inputs/constants'
import { SectionTitle } from './ProjectsCreate'
import ProjectFundFields from './ProjectFundFields'
import { FieldErrorIndicator, NavigationButton } from '../HelperComponents'
import { changeField, changeHandler } from './SpecificFieldsHelpers'
import {
  canEditField,
  canGoToSecondStep,
  canViewField,
  getProjectDuration,
  hasFields,
  onTextareaFocus,
} from '../utils'
import {
  tableColumns,
  considerationOpts,
  defaultProps,
  defaultPropsSimpleField,
  textAreaClassname,
  disabledClassName,
} from '../constants'
import {
  CrossCuttingFields,
  ProjectDataProps,
  ProjectData,
  ProjectTabSetters,
  ProjectTypeApi,
  FieldOptsType,
} from '../interfaces'
import { ProjectSubSectorType } from '@ors/types/api_project_subsector.ts'
import { useStore } from '@ors/store'

import { filter, find, includes, omit, some } from 'lodash'
import { TextareaAutosize, Divider } from '@mui/material'
import cx from 'classnames'
import dayjs from 'dayjs'

const ProjectCrossCuttingFields = ({
  projectData,
  setProjectData,
  project,
  errors = {},
  nextStep,
  setCurrentTab,
  fieldsOpts,
  specificFieldsLoaded,
  postExComUpdate,
  isV3ProjectEditable,
  mode,
}: ProjectDataProps &
  ProjectTabSetters & {
    project?: ProjectTypeApi
    nextStep: number
    specificFieldsLoaded: boolean
    postExComUpdate: boolean
    isV3ProjectEditable: boolean
    mode: string
    fieldsOpts: FieldOptsType
  }) => {
  const userSlice = useStore((state) => state.user)
  const { agency_id } = userSlice.data
  const { consumptionLevelStatuses } = useContext(ProjectsDataContext)

  const {
    crtProjectTypesOpts,
    projectTypes,
    crtSectorsOpts,
    sectors,
    crtSubsectorsOpts,
    subsectors,
  } = fieldsOpts
  const sectionIdentifier = 'crossCuttingFields'
  const { projIdentifiers } = projectData
  const crossCuttingFields = projectData[sectionIdentifier]
  const {
    project_type,
    sector,
    subsector_ids,
    consumption_level_status,
    title,
    description,
    project_start_date,
    project_end_date,
    project_duration,
    blanket_or_individual_consideration,
  } = crossCuttingFields
  const { submission_status } = project || {}

  const isProjectApproved = mode === 'edit' && submission_status === 'Approved'

  const { projectFields, viewableFields, editableFields } = useStore(
    (state) => state.projectFields,
  )

  const isV3Project = postExComUpdate || isV3ProjectEditable
  const canViewAboutSection =
    canViewField(viewableFields, 'title') ||
    canViewField(viewableFields, 'description')
  const canViewDetailsSection = hasFields(
    projectFields,
    viewableFields,
    'Cross-Cutting',
    false,
    ['title', 'description'],
  )

  const sectionDefaultProps = {
    ...defaultProps,
    FieldProps: {
      className: defaultProps.FieldProps.className + ' w-full',
    },
  }
  const defaultPropsDateInput = {
    className: 'BPListUpload !ml-0 h-10 w-40 !flex-grow-0',
  }

  const areInvalidFields =
    !isProjectApproved &&
    !(canGoToSecondStep(projIdentifiers, agency_id) && project_type && sector)
  const isNextDisabled = areInvalidFields || !specificFieldsLoaded
  const isStartDateDisabled =
    (isProjectApproved && (postExComUpdate || !!project?.project_start_date)) ||
    !canEditField(editableFields, 'project_start_date')

  const handleChangeSubSector = (subsectors: ProjectSubSectorType[]) => {
    setProjectData(
      (prevData) => ({
        ...prevData,
        [sectionIdentifier]: {
          ...prevData[sectionIdentifier],
          subsector_ids: subsectors.map((subsector) => subsector.id) ?? [],
        },
      }),
      'subsector',
    )
  }

  const getFieldDefaultProps = (field: string) => {
    return {
      ...{
        ...defaultPropsSimpleField,
        className: cx(defaultPropsSimpleField.className, '!m-0 h-10 !py-1', {
          [disabledClassName]:
            !canEditField(editableFields, field) ||
            field === 'project_duration',
        }),
      },
    }
  }

  return (
    <>
      {canViewAboutSection && (
        <>
          <SectionTitle>About</SectionTitle>
          <div className="flex flex-col gap-y-2">
            {canViewField(viewableFields, 'title') && (
              <div>
                <Label>{tableColumns.title}</Label>
                <div className="flex items-center">
                  <SimpleInput
                    id="title"
                    value={title}
                    onFocus={onTextareaFocus}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      changeHandler['text']<ProjectData, CrossCuttingFields>(
                        event,
                        'title',
                        setProjectData,
                        sectionIdentifier,
                      )
                    }
                    disabled={!canEditField(editableFields, 'title')}
                    type="text"
                    {...getFieldDefaultProps('title')}
                    containerClassName={
                      defaultPropsSimpleField.containerClassName +
                      ' w-full max-w-[55rem]'
                    }
                  />
                  <div className="w-8">
                    <FieldErrorIndicator errors={errors} field="title" />
                  </div>
                </div>
              </div>
            )}
            {canViewField(viewableFields, 'description') && (
              <div>
                <Label>{tableColumns.description} (max 1000 characters)</Label>
                <div className="flex items-center">
                  <TextareaAutosize
                    value={description}
                    onFocus={onTextareaFocus}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      changeHandler['text']<ProjectData, CrossCuttingFields>(
                        event,
                        'description',
                        setProjectData,
                        sectionIdentifier,
                      )
                    }
                    disabled={!canEditField(editableFields, 'description')}
                    className={cx(textAreaClassname, 'max-w-[64rem]')}
                    maxLength={1000}
                    style={STYLE}
                    minRows={6}
                  />
                  <FieldErrorIndicator errors={errors} field="description" />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {canViewAboutSection && canViewDetailsSection && (
        <Divider className="my-6" />
      )}

      {canViewDetailsSection && (
        <>
          <SectionTitle>Details</SectionTitle>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-wrap gap-x-20 gap-y-3">
              {canViewField(viewableFields, 'project_type') && (
                <div className="flex-shrink basis-[18.5rem]">
                  <Label>{tableColumns.type}</Label>
                  <div className="flex items-center">
                    <Field
                      widget="autocomplete"
                      options={crtProjectTypesOpts}
                      value={
                        some(projectTypes, { id: project_type })
                          ? project_type
                          : null
                      }
                      onChange={(_: React.SyntheticEvent, value) =>
                        changeHandler['drop_down']<
                          ProjectData,
                          CrossCuttingFields
                        >(
                          value,
                          'project_type',
                          setProjectData,
                          sectionIdentifier,
                        )
                      }
                      getOptionLabel={(option: any) =>
                        getOptionLabel(projectTypes, option)
                      }
                      disabled={
                        (isV3Project &&
                          (postExComUpdate ||
                            (!!project?.project_type_id && !!project_type))) ||
                        !specificFieldsLoaded ||
                        !canEditField(editableFields, 'project_type')
                      }
                      {...sectionDefaultProps}
                    />
                    <div className="w-8">
                      <FieldErrorIndicator
                        errors={errors}
                        field="project_type"
                      />
                    </div>
                  </div>
                </div>
              )}
              {canViewField(viewableFields, 'sector') && (
                <div className="flex-shrink basis-[18.5rem]">
                  <Label>{tableColumns.sector}</Label>
                  <div className="flex items-center">
                    <Field
                      widget="autocomplete"
                      options={crtSectorsOpts}
                      value={some(sectors, { id: sector }) ? sector : null}
                      onChange={(_, value) =>
                        changeHandler['drop_down']<
                          ProjectData,
                          CrossCuttingFields
                        >(value, 'sector', setProjectData, sectionIdentifier)
                      }
                      getOptionLabel={(option) =>
                        getOptionLabel(sectors, option)
                      }
                      disabled={
                        (isV3Project &&
                          (postExComUpdate ||
                            (!!project?.sector_id && !!sector))) ||
                        !specificFieldsLoaded ||
                        !canEditField(editableFields, 'sector')
                      }
                      {...sectionDefaultProps}
                    />
                    <div className="w-8">
                      <FieldErrorIndicator errors={errors} field="sector" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-x-20 gap-y-3">
              {canViewField(viewableFields, 'subsectors') && (
                <div className="flex-shrink basis-[40.25rem]">
                  <Label>{tableColumns.subsectors}</Label>
                  <div className="flex items-center">
                    <div className="w-full">
                      <Field
                        widget="autocomplete"
                        multiple={true}
                        options={crtSubsectorsOpts}
                        value={
                          filter(subsectors, (subsector) =>
                            includes(subsector_ids, subsector.id),
                          ) as ProjectSubSectorType[]
                        }
                        onChange={(_, value) =>
                          handleChangeSubSector(value as ProjectSubSectorType[])
                        }
                        getOptionLabel={(option) =>
                          getOptionLabel(subsectors, option)
                        }
                        disabled={!canEditField(editableFields, 'subsectors')}
                        FieldProps={{ className: 'w-full BPListUpload mb-0' }}
                      />
                    </div>
                    <FieldErrorIndicator
                      errors={errors}
                      field="subsector_ids"
                    />
                  </div>
                </div>
              )}
              {canViewField(viewableFields, 'consumption_level_status') && (
                <div>
                  <Label>{tableColumns.consumption_level_status}</Label>
                  <div className="flex items-center">
                    <Field
                      widget="autocomplete"
                      options={consumptionLevelStatuses}
                      value={
                        find(consumptionLevelStatuses, {
                          id: consumption_level_status,
                        }) || null
                      }
                      onChange={(_, value) =>
                        changeHandler['drop_down']<
                          ProjectData,
                          CrossCuttingFields
                        >(
                          value,
                          'consumption_level_status',
                          setProjectData,
                          sectionIdentifier,
                        )
                      }
                      getOptionLabel={(option: any) =>
                        getOptionLabel(consumptionLevelStatuses, option)
                      }
                      disabled={
                        !canEditField(
                          editableFields,
                          'consumption_level_status',
                        )
                      }
                      {...defaultProps}
                    />
                    <FieldErrorIndicator
                      errors={errors}
                      field="consumption_level_status"
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              className={cx('flex w-fit flex-wrap gap-x-20 gap-y-2', {
                'grid-cols-2 md:grid': !(
                  mode === 'edit' && submission_status === 'Approved'
                ),
              })}
            >
              <ProjectFundFields
                {...{
                  projectData,
                  setProjectData,
                  project,
                  errors,
                  mode,
                  postExComUpdate,
                }}
                type="crossCutting"
              />
            </div>
            <div className="flex w-fit grid-cols-3 flex-wrap gap-x-20 gap-y-2 md:grid">
              {canViewField(viewableFields, 'project_start_date') && (
                <div>
                  <Label>{tableColumns.project_start_date}</Label>
                  <div className="flex items-center">
                    <DateInput
                      id="project_start_date"
                      value={project_start_date as string}
                      onChange={(event) => {
                        const startDate = event.target.value || null

                        changeField(
                          startDate,
                          'project_start_date',
                          setProjectData,
                          sectionIdentifier,
                        )

                        changeField(
                          getProjectDuration({
                            project_start_date: startDate,
                            project_end_date,
                          }),
                          'project_duration',
                          setProjectData,
                          sectionIdentifier,
                        )
                      }}
                      disabled={isStartDateDisabled}
                      formatValue={(value) => dayjs(value).format('DD/MM/YYYY')}
                      className={cx(defaultPropsDateInput.className, {
                        [disabledClassName]: isStartDateDisabled,
                      })}
                    />
                    <div className="w-8">
                      <FieldErrorIndicator
                        errors={errors}
                        field="project_start_date"
                      />
                    </div>
                  </div>
                </div>
              )}
              {canViewField(viewableFields, 'project_end_date') && (
                <div>
                  <Label>{tableColumns.project_end_date}</Label>
                  <div className="flex items-center">
                    <DateInput
                      id="project_end_date"
                      value={project_end_date as string}
                      onChange={(event) => {
                        const endDate = event.target.value || null

                        changeField(
                          endDate,
                          'project_end_date',
                          setProjectData,
                          sectionIdentifier,
                        )
                        if (mode === 'edit' && (project?.version ?? 0) >= 3) {
                          changeField(
                            endDate,
                            'date_completion',
                            setProjectData,
                            'approvalFields',
                          )
                        }

                        changeField(
                          getProjectDuration({
                            project_start_date,
                            project_end_date: endDate,
                          }),
                          'project_duration',
                          setProjectData,
                          sectionIdentifier,
                        )
                      }}
                      disabled={
                        !canEditField(editableFields, 'project_end_date')
                      }
                      formatValue={(value) => dayjs(value).format('DD/MM/YYYY')}
                      className={cx(defaultPropsDateInput.className, {
                        [disabledClassName]: !canEditField(
                          editableFields,
                          'project_end_date',
                        ),
                      })}
                    />
                    <FieldErrorIndicator
                      errors={errors}
                      field="project_end_date"
                    />
                  </div>
                </div>
              )}
              {canViewField(viewableFields, 'project_duration') && (
                <div>
                  <Label>{tableColumns.project_duration}</Label>
                  <div className="flex items-center">
                    <FormattedNumberInput
                      id="project_duration"
                      value={project_duration ?? ''}
                      withoutDefaultValue={true}
                      decimalDigits={0}
                      disabled={true}
                      {...omit(
                        getFieldDefaultProps('project_duration'),
                        'containerClassName',
                      )}
                    />
                    <FieldErrorIndicator
                      errors={errors}
                      field="project_duration"
                    />
                  </div>
                </div>
              )}
            </div>
            {canViewField(
              viewableFields,
              'blanket_or_individual_consideration',
            ) && (
              <div>
                <Label>
                  {tableColumns.blanket_or_individual_consideration}
                </Label>
                <div className="flex items-center">
                  <Field
                    widget="autocomplete"
                    options={considerationOpts}
                    value={
                      considerationOpts.find(
                        (opt) => opt.id === blanket_or_individual_consideration,
                      ) ?? null
                    }
                    onChange={(_, value) =>
                      changeHandler['drop_down']<
                        ProjectData,
                        CrossCuttingFields
                      >(
                        value,
                        'blanket_or_individual_consideration',
                        setProjectData,
                        sectionIdentifier,
                      )
                    }
                    getOptionLabel={(option: any) =>
                      getOptionLabel(considerationOpts, option, 'value')
                    }
                    disabled={
                      !canEditField(
                        editableFields,
                        'blanket_or_individual_consideration',
                      )
                    }
                    {...{
                      ...defaultProps,
                      FieldProps: {
                        className:
                          defaultProps.FieldProps.className + ' w-[13.5rem]',
                      },
                    }}
                  />
                  <FieldErrorIndicator
                    errors={errors}
                    field="blanket_or_individual_consideration"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-2.5">
        <NavigationButton type="previous" {...{ setCurrentTab }} />
        <NavigationButton
          nextTab={nextStep - 1}
          isBtnDisabled={isNextDisabled}
          setCurrentTab={setCurrentTab}
        />
      </div>
    </>
  )
}

export default ProjectCrossCuttingFields
